<template>
  <div class="content container">
    <h1 class="heading">Installation</h1>

    <form ref="form" @submit.prevent="saveChanges" v-if="!loading" class="edit-form">
      <h2 class="subheading">Details</h2>
      <div class="flex flex-row items-center mb-8">
        <label for="public" class="flex flex-row flex-no-wrap items-center">
          <span class="styled-checkbox">
            <input
              id="public"
              name="public"
              v-model="installation.public"
              type="checkbox"
            /><span></span>
          </span>

          <span class="flex-1">
            <span :style="{ opacity: installation.public ? 0 : 1 }">Not</span> Public
          </span>
        </label>

        <div class="flex-1 ml-2">
          <p class="hint pb-0" v-if="!installation.public">
            Installation will be <strong>hidden</strong> from external sites using the API. eg.
            <a href="https://oxford.uk100.org/" target="_blank">uk100</a>
          </p>
          <p class="hint pb-0" v-if="installation.public">
            Installation will be <strong>shown</strong> in external sites using the API. eg.
            <a href="https://oxford.uk100.org/" target="_blank">uk100</a>
          </p>
        </div>
      </div>

      <div v-if="!isEditing" :class="{ invalid: installationsWithSameId.length }">
        <label>Installation unique identifiable number</label>
        <p class="hint">
          Leave as <strong>-1</strong> for the system to automatically assing a random and unique
          number.
        </p>

        <money
          v-model="installation._id"
          v-bind="moneyIdOptions"
          @input="getInstallationsWithSameId"
        ></money>
      </div>

      <transition name="fade">
        <div
          v-if="!isEditing && installationsWithSameId.length"
          class="border-red-dark border-l-8 bg-red-lightest py-8 pl-8 mb-8"
        >
          <h3>There is an installation with that id.</h3>
          <p class="hint">Each installation <strong>must</strong> have a unique id.</p>
          <div v-for="i in installationsWithSameId" :key="i._id">
            <a :href="`/admin/installation/${i._id}`" target="_blank">{{ i._id }} - {{ i.name }}</a>
          </div>
        </div>
      </transition>

      <div :class="{ invalid: isInvalid('name') }">
        <label class="label--required">Name</label>
        <input
          v-model="installation.name"
          required
          type="text"
          @input="getInstallationsWithSameName"
        />
      </div>

      <transition name="fade">
        <div
          v-if="installationsWithSameName.length"
          class="border-green-dark border-l-8 bg-green-lightest py-8 pl-8 mb-8"
        >
          <h3>
            There are {{ installationsWithSameName.length }} installations with the same name.
          </h3>
          <p class="hint">Installations with the same name are grouped into one.</p>
          <div v-for="i in installationsWithSameName" :key="i._id">
            <a :href="`/admin/installation/${i._id}`" target="_blank">{{ i._id }} - {{ i.name }}</a>
          </div>
        </div>
      </transition>

      <div>
        <label>URL</label> <input v-model="installation.url" type="text" placeholder="https://" />
      </div>

      <div>
        <label>Info</label>
        <textarea
          v-model="installation.info"
          placeholder="Some extra information about the installation."
        ></textarea>
      </div>

      <hr />

      <h2 class="subheading">Ownership</h2>

      <div class="mb-8" :class="{ invalid: isInvalid('owner') }">
        <label class="label--required">Owner</label>
        <multiselect
          v-model="installation.owner"
          :options="selectOptions.owner"
          :taggable="true"
          tag-placeholder="Press enter to add a new owner"
          @tag="addOwner"
        ></multiselect>
      </div>

      <div class="mb-8" :class="{ invalid: isInvalid('ownershipType') }">
        <label class="label--required">Ownership type</label>
        <multiselect
          v-model="installation.ownershipType"
          :options="selectOptions.ownershipType"
          :taggable="true"
          tag-placeholder="Press enter to add a new ownership type"
          @tag="addOwnershipType"
        ></multiselect>
      </div>

      <div :class="{ invalid: isInvalid('commissioned') }">
        <label class="label--required">Commissioned</label>
        <input v-model="installation.commissioned" required type="text" />
      </div>

      <hr />

      <h2 class="subheading">Geographical location</h2>

      <div class="mb-8">
        <label for="postcode">Post Code</label>
        <input
          id="postcode"
          name="postcode"
          type="text"
          v-model="postCode"
          @keyup="getPostCodeCoordinates"
          placeholder="Insert a post code..."
        />

        <div class="hint -mt-4">
          Insert a Post Code to populate:<strong>local authority</strong>
          <span v-if="!installation.location">, <strong>location</strong></span
          >, <strong>latitude</strong>, <strong>longitude</strong>
        </div>
      </div>
      <div class="flex flex-row">
        <div class="flex-1 pr-8 mb-8" :class="{ invalid: isInvalid('localAuthority') }">
          <label class="label--required">Local Authority</label>
          <multiselect
            v-model="installation.localAuthority"
            :options="selectOptions.localAuthority"
          ></multiselect>
        </div>

        <div class="flex-1 mb-8">
          <label>Location</label>
          <multiselect
            v-model="installation.location"
            :options="selectOptions.location"
            :taggable="true"
            tag-placeholder="Press enter to add a new location"
            @tag="addLocation"
          ></multiselect>
        </div>
      </div>

      <h2 class="subheading">Position on map</h2>

      <div class="flex flex-row">
        <div class="flex-1">
          <p class="hint">...or add the coordinates</p>

          <div class="flex flex-row">
            <div class="flex-1 mr-4" :class="{ invalid: isInvalid('lat') }">
              <label class="label--required">Latitude</label>
              <input v-model="installation.lat" required type="text" />
            </div>

            <div class="flex-1">
              <label class="label--required" :class="{ invalid: isInvalid('lng') }"
                >Longitude</label
              >
              <input v-model="installation.lng" required type="text" />
            </div>
          </div>

          <transition name="fade">
            <button
              v-if="coordinatesHaveChanged"
              @click.prevent="resetToOriginalCoords();"
              class="btn--large"
            >
              Reset coordinates
            </button>
          </transition>
        </div>

        <div class="flex-1 pl-8 pb-8">
          <p class="hint mt-8">...or click and drag the dot to set new coordinates.</p>
          <div class="flex-1 relative map-container z-0">
            <l-map :center="mapCenter" :zoom="22" :options="{ scrollWheelZoom: false }">
              <l-tile-layer :url="url" />
              <l-marker
                @update:latLng="movedMarker"
                :lat-lng="markerCoords"
                :draggable="true"
              ></l-marker>
            </l-map>
            <button
              class="btn"
              :class="{ 'btn--main': tiles === 'satellite' }"
              @click.prevent="toggleTiles('satellite');"
            >
              Satellite view
            </button>
            <button
              class="btn ml-2"
              :class="{ 'btn--main': tiles !== 'satellite' }"
              @click.prevent="toggleTiles();"
            >
              Map view
            </button>
          </div>
        </div>
      </div>

      <hr />

      <h2 class="subheading">Energy details</h2>

      <div>
        <div class="mb-8" :class="{ invalid: isInvalid('type') }">
          <label class="label--required">Type of installation</label>
          <multiselect
            v-model="installation.type"
            :options="selectOptions.type"
            :allow-empty="false"
            :searchable="false"
            @input="installationTypeChanged"
          ></multiselect>
        </div>

        <transition-group name="fade">
          <div v-if="installation.type === 'Renewable Energy'" key="renewable">
            <div class="mb-8" :class="{ invalid: isInvalid('energyType') }">
              <label class="label--required">Type of Energy</label>

              <multiselect
                v-model="installation.energyType"
                :allow-empty="false"
                :options="selectOptions.energyType"
                :searchable="false"
              ></multiselect>
            </div>

            <div class="mb-8">
              <label>API source</label>
              <multiselect
                v-model="installation.source"
                :options="selectOptions.source"
                :searchable="false"
              ></multiselect>
            </div>

            <div class="flex flex-row max-w-md">
              <div class="flex-1 mr-4" :class="{ invalid: isInvalid('annualPredictedGeneration') }">
                <label class="label--required">Annual Predicted Generation</label>
                <money
                  v-model="installation.annualPredictedGeneration"
                  v-bind="moneykWhOptions"
                ></money>
              </div>

              <div class="flex-1" :class="{ invalid: isInvalid('capacity') }">
                <label class="label--required">Capacity</label>
                <money v-model="installation.capacity" v-bind="capacityOptions"></money>
              </div>
            </div>
          </div>
          <div v-else key="efficiency">
            <ul class="m-0 p-0 max-w-sm">
              <li
                class="list-reset mb-2"
                v-for="intervention in selectOptions.interventions"
                :key="intervention"
              >
                <label
                  class="flex flex-row flex-no-wrap items-center"
                  v-if="intervention !== 'co2' && intervention !== 'total'"
                >
                  <span class="styled-checkbox">
                    <input
                      v-model="installation.interventions[intervention]"
                      type="checkbox"
                      true-value="1"
                      false-value="0"
                    />
                    <span></span>
                  </span>

                  <span class="flex-1">{{ intervention }}</span>
                </label>
                <div v-else class="flex flex-row flex-no-wrap items-center content-center">
                  <div class="flex-1 capitalize whitespace-no-wrap">{{ intervention }} savings</div>

                  <money
                    v-if="intervention === 'total'"
                    v-model="installation.interventions[intervention]"
                    v-bind="moneykWhOptions"
                    class="ml-2"
                  ></money>
                  <money
                    v-else
                    v-model="installation.interventions[intervention]"
                    v-bind="moneyCo2Options"
                    class="ml-2"
                  ></money>
                </div>
              </li>
            </ul>
          </div>
        </transition-group>
      </div>

      <hr />

      <h2 class="subheading">QuickBase Integration</h2>
      <p class="hint">
        If this installation's data is to appear in QuickBase, add its export and generation IDs.
      </p>

      <div class="flex flex-row max-w-md">
        <div class="flex-1 mr-4">
          <label>Export ID</label>
          <money v-model="installation.quickbase.export" v-bind="moneyIdOptions"></money>
        </div>

        <div class="flex-1">
          <label>Generation ID</label>
          <money v-model="installation.quickbase.generation" v-bind="moneyIdOptions"></money>
        </div>
      </div>

      <div class="mt-8">
        <button class="btn btn--xl btn--main" type="submit">{{ saveButtonCopy }}</button>
      </div>
    </form>
  </div>
</template>

<script>
import axios from '@/axios';
import jsonp from 'jsonp';
import { L, LMap, LTileLayer, LMarker } from 'vue2-leaflet';
import { Money } from 'v-money';

// import 'leaflet.icon.glyph';

delete L.Icon.Default.prototype._getIconUrl;

L.Icon.Default.mergeOptions({
  iconRetinaUrl: require('leaflet/dist/images/marker-icon-2x.png'),
  iconUrl: require('leaflet/dist/images/marker-icon.png'),
  shadowUrl: require('leaflet/dist/images/marker-shadow.png')
});

import 'leaflet/dist/leaflet.css';

export default {
  name: 'addedit',
  components: {
    LMap,
    LTileLayer,
    LMarker,
    Money
  },
  data() {
    return {
      mapboxAccessToken:
        'pk.eyJ1IjoicGlyZW5hcSIsImEiOiJjaWVtbzl2eXgwMDFuc3Rra3RuaWlnNzMxIn0.HxN1ugyk0JzH46gFnb6mXA',
      mapStyle: 'mapbox://styles/mapbox/light-v9',
      postCode: null,
      installationsWithSameId: [],
      installationsWithSameName: [],
      errors: [],
      initialCoords: {
        lat: 51.85,
        lng: -1.34
      },
      // url: 'http://{s}.tile.osm.org/{z}/{x}/{y}.png',
      tiles: 'satellite',
      mapCoords: {
        lat: 51.85,
        lng: -1.34
      },
      installation: {
        _id: -1,
        public: false,
        name: null,
        info: null,
        url: null,
        owner: null,
        ownershipType: null,
        commissioned: null,
        localAuthority: null,
        location: null,
        lat: 51.85,
        lng: -1.34,
        type: 'Renewable Energy',
        energyType: null,
        source: null,
        annualPredictedGeneration: 0,
        capacity: 0,
        interventions: {},
        quickbase: {
          export: 0,
          generation: 0
        }
      },
      selectOptions: {},
      loading: true,
      error: null,
      moneyIdOptions: {
        decimal: '',
        thousands: '',
        prefix: '',
        suffix: '',
        precision: 0,
        masked: false
      },
      moneykWhOptions: {
        decimal: '.',
        thousands: ',',
        prefix: ' ',
        suffix: ' (kWh)',
        precision: 0,
        masked: false
      },
      moneyCo2Options: {
        decimal: '.',
        thousands: ',',
        prefix: '',
        suffix: ' tonnes',
        precision: 0,
        masked: false
      },
      capacityOptions: {
        decimal: '.',
        thousands: ',',
        prefix: '',
        suffix: ' (kW)',
        precision: 2,
        masked: false
      }
    };
  },
  created() {
    // fetch the data when the view is created and the data is
    // already being observed
    this.fetchData();
  },
  watch: {
    // call again the method if the route changes
    $route: 'fetchData'
  },
  computed: {
    url() {
      if (this.tiles === 'satellite') {
        return 'https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}';
      }

      return 'https://server.arcgisonline.com/ArcGIS/rest/services/World_Topo_Map/MapServer/tile/{z}/{y}/{x}';
    },
    saveButtonCopy() {
      return this.isEditing ? 'Save Changes to installation' : 'Add new installation';
    },
    formSubmitURL() {
      return this.isEditing
        ? `/api/v2/installations/${this.$route.params.id}`
        : '/api/v2/installations';
    },
    isEditing() {
      return typeof this.$route.params.id !== 'undefined';
    },
    markerCoords() {
      return L.latLng(this.installation.lat, this.installation.lng);
    },
    mapCenter() {
      return L.latLng(this.mapCoords.lat, this.mapCoords.lng);
    },
    coordinatesHaveChanged() {
      return (
        this.installation.lat !== this.initialCoords.lat ||
        this.installation.lng !== this.initialCoords.lng
      );
    }
  },
  methods: {
    async getInstallationsWithSameName() {
      const installationsWithName = await axios.get(
        `/api/v2/installations/with-same/?name=${this.installation.name}&exclude=${
          this.installation._id
        }`
      );

      this.installationsWithSameName = installationsWithName.data;
    },
    async getInstallationsWithSameId() {
      const installationsWithName = await axios.get(
        `/api/v2/installations/with-same/?_id=${this.installation._id}`
      );

      this.installationsWithSameId = installationsWithName.data;
    },
    addOption(name, newOption) {
      this.selectOptions[name].push(newOption);
      this.installation[name] = newOption;
    },
    addLocation(option) {
      this.addOption('location', option);
    },
    addOwner(option) {
      this.addOption('owner', option);
    },
    addOwnershipType(option) {
      this.addOption('ownershipType', option);
    },
    movedMarker(coords) {
      if (this.$refs.form.querySelector('#postcode:focus')) {
        // user is typing a postcode
        return;
      }

      this.postCode = null;
      this.installation.lng = coords.lng;
      this.installation.lat = coords.lat;
      this.mapCoords = {
        lat: this.installation.lat,
        lng: this.installation.lng
      };
    },
    resetToOriginalCoords() {
      this.postCode = null;
      this.installation.lat = this.initialCoords.lat;
      this.installation.lng = this.initialCoords.lng;

      this.mapCoords = {
        lat: this.installation.lat,
        lng: this.installation.lng
      };
    },
    toggleTiles(name = 'map') {
      const options = ['satellite', 'map'];
      this.tiles = options.includes(name) ? name : 'map';
    },
    isInvalid(propertyName) {
      return (
        this.installation[propertyName] === null || this.installation[propertyName].length === 0
      );
    },
    async getPostCodeCoordinates() {
      if (this.postCode.length < 3) {
        return;
      }

      const url = `//api.postcodes.io/postcodes/${this.postCode}`;
      await jsonp(url, null, (err, data) => {
        if (err) {
          // this.$snotify.error(err.message, {
          //   pauseOnHover: true,
          //   showProgressBar: true,
          //   timeout: 3000
          // });
        } else {
          if (!data.result) {
            return;
          }

          this.installation.lat = Math.round(data.result.latitude * 10000) / 10000;
          this.installation.lng = Math.round(data.result.longitude * 10000) / 10000;

          this.mapCoords = {
            lat: this.installation.lat,
            lng: this.installation.lng
          };

          this.installation.localAuthority = this.parseLA(data.result.admin_district);

          if (!this.installation.location) {
            this.installation.location = data.result.parish;
          }
        }
      });
    },
    parseLA(name) {
      // we need to do this because we preppended stuff to them :-(
      const available = {
        Buckinghamshire: 'Buckinghamshire',
        Cherwell: 'Cherwell District Council',
        Oxford: 'Oxford City Council',
        'South Oxfordshire': 'South Oxfordshire District Council',
        Swindon: 'Swindon Borough Council',
        'Vale of White Horse': 'Vale of White Horse District Council',
        'West Oxfordshire': 'West Oxfordshire District Council'
      };

      return available[name] ? available[name] : name;
    },
    installationTypeChanged() {
      this.installation.interventions = {};
      this.installation.annualPredictedGeneration = 0;
      this.installation.capacity = 0;
      this.installation.energyType = null;
      this.installation.source = null;
    },
    async fetchData() {
      // Get all the available options for
      // LAs
      // owner
      // ownershipTypes
      // source
      // energyTypes
      // supportedBy
      // interventions
      // locations
      // types
      const optionsCall = axios.get(`/api/v2/installations/options`);
      const opts = await optionsCall.catch(() => false);

      if (this.isEditing) {
        // If we are editing, bring in the installation's details
        const installationCall = axios.get(`/api/v2/installations/full/${this.$route.params.id}`);
        const API_call = await installationCall.catch(() => false);
        this.installation = this.convertInstallationServerToClient(API_call.data);

        this.initialCoords = {
          lat: this.installation.lat,
          lng: this.installation.lng
        };

        this.mapCoords = {
          lat: this.installation.lat,
          lng: this.installation.lng
        };

        // check and see if there are more items with this name
        this.getInstallationsWithSameName();
      }

      this.loading = false;

      // if no interventions, set it as a blank object
      this.installation.interventions = this.installation.interventions || {};

      this.selectOptions = opts.data;
    },
    async saveChanges() {
      const form = this.$refs.form;
      const invalidFields = this.getInvalidFields(form);

      if (invalidFields && invalidFields.length > 0) {
        this.scrollToField(invalidFields[0]);
        return false;
      }

      this.$snotify.async(
        'Submittig form',
        '',
        async () =>
          new Promise(async (resolve, reject) => {
            await axios
              .post(this.formSubmitURL, this.installation)
              .then(res => {
                if (!this.isEditing) {
                  const newId = res.data._id;
                  this.$router.push(`/admin/installation/${newId}`);
                }

                resolve({
                  body: 'Changes successfully saved',
                  config: {
                    pauseOnHover: true,
                    showProgressBar: true,
                    timeout: 3000
                  }
                });
              })
              .catch(err => {
                const errorMessage = err.response.data.message || 'Something went wrong.';

                reject({
                  html: `<div class="snotifyToast__body">Changes not saved.<br>${errorMessage}</div><div class="snotify-icon snotify-icon--error"></div>`,
                  config: {
                    pauseOnHover: true,
                    showProgressBar: true,
                    timeout: 5000
                  }
                });
              });
          })
      );
    },
    getInvalidFields(form) {
      return form.querySelectorAll('.invalid, input:invalid');
    },
    scrollToField(field) {
      if (!field) {
        return;
      }

      field.scrollIntoView({
        behavior: 'smooth'
      });
    },
    convertInstallationServerToClient(installation) {
      if (installation.annualPredictedGeneration) {
        installation.annualPredictedGeneration = installation.annualPredictedGeneration / 1000;
      }

      if (installation.capacity) {
        installation.capacity = installation.capacity / 1000;
      }

      if (installation.interventions && installation.interventions.total) {
        installation.interventions.total = installation.interventions.total / 1000;
      }

      return installation;
    }
  }
};
</script>

<style lang="sass">
.map-container
  height: 300px

.v-icon
  background: #cc8209
  border: 0.5px solid #fff
  box-shadow: 0 1px 2px 0 rgba(0, 0, 0, 0.5)
  border-radius: 50%
  cursor: move
  display: block
  height: 12px
  transform: translate(-50%, -50%)
  width: 12px

input:invalid,
.invalid .multiselect,
.invalid textarea,
.invalid input[type='number'],
.invalid input[type='text'],
.invalid input.v-money
  @apply bg-red-lightest
  box-shadow: 0 0 2px red
  outline: 0

.input-number
  @apply border
  @apply border-grey-light
  @apply inline-block
  @apply leading-normal
  @apply max-w-xs
  @apply m-0
  @apply px-2
  @apply py-2
  @apply rounded-sm

.edit-form .multiselect
  @apply max-w-sm

.edit-form .multiselect__tags
  @apply rounded-sm

.styled-checkbox
  cursor: pointer
  position: relative

  input
    height: 100%
    left: 0
    opacity: 0.01
    position: absolute
    top: 0
    width: 100%

  input + span
    background: #c6c5c5
    border-radius: 7px
    display: inline-block
    height: 14px
    margin: 6px 15px 0 0
    position: relative
    transition: all .1s ease-in
    width: 34px

    &:before
      background: #f1f1f1
      border: 1px solid transparent
      box-shadow: 0 0 1px 0 rgba(0,0,0,.12),0 1px 1px 0 rgba(0,0,0,.24)
      border-radius: 50%
      content: ''
      left: 0
      height: 18px
      position: absolute
      top: 50%
      transform: translate(-2px,-50%)
      transform-origin: 50% 50%
      transition: transform .1s ease-in
      width: 18px

  input:checked + span
    background: #92e7bd

  input:checked + span:before
    background: #24ce7b
    transform: translate(17px, -50%)
</style>
